import LoginDialog from "./LoginDialog.js";
import { postJSON } from "../helpers.js";
import RegisterDialog from "./RegisterDialog.js";
import VerifyDialog from "./VerifyDialog.js";
import LoginPromptDialog from "./LoginPromptDialog.js";
import ProfileDialog from "./ProfileDialog.js";

const handleThirdPartyAuthenticationFailure = (error) => {
  if (process.env.NODE_ENV === "development") console.error("Third Party Login Failed", error);

  // Special handling for FedCM being blocked
  if (error && error.error === "one_tap_skipped") {
    // alert(
    //   "A Google bejelentkezés használatához engedélyezd a harmadik fél általi bejelentkezést a böngésző beállításaiban."
    // );
  }

  // settingsForm.enable();
};

const loginWithThirdPartyUser = async (thirdPartyUser) => {
  try {
    const data = await postJSON("/api/login/" + thirdPartyUser.service + "/", {
      id_token: thirdPartyUser.id_token,
      name: thirdPartyUser.name,
      picture: thirdPartyUser.picture,
      referer: window.location.href,
    });

    loginDialog.close();
    if (data.status == "not-registered") {
      openRegisterDialog(thirdPartyUser);
    } else if (data.status == "not-verified") {
      openRegisterDialog(thirdPartyUser);
    } else if (data.status == "not-consent") {
      thirdPartyUser.email = data.user.email;
      openRegisterDialog(thirdPartyUser, "Hozzájárulás", "Hozzájárulok");
    } else if (data.status == "registered") {
      openVerifyDialog(data);
    } else if (data.status == "authenticated") {
      loginSuccessful(data, true);
    } else if (data.status == "login-error") {
      if (process.env.NODE_ENV === "development") console.log("login-error", data);
      /* TODO: maybe we should give some feedback here, pls retry or else */
      logoutSuccessful();
    } else {
      if (process.env.NODE_ENV === "development") console.log("Unknown login status", data);
      /* what could have happened here? */
      logoutSuccessful();
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") console.log("thirdPartyAuthenticationSuccessful", error);
    logoutSuccessful();
  }
};

let user = null;
let apiToken = null;
let calendarToken = null;
let userDataTimestamp = null;

// User data TTL: 2 minutes
const USER_DATA_TTL_MS = 2 * 60 * 1000;

/**
 * Get current user, automatically refreshing if data is stale
 * @returns {Promise<Object|null>} User object or null if not logged in
 */
export const getCurrentUser = async () => {
  if (!user || !apiToken) {
    return null;
  }

  // Check if user data is stale
  const now = Date.now();
  if (userDataTimestamp && now - userDataTimestamp < USER_DATA_TTL_MS) {
    // Data is fresh, return cached
    return user;
  }

  // Data is stale, refresh it
  try {
    const freshUser = await refreshUserData();
    return freshUser;
  } catch (error) {
    if (process.env.NODE_ENV === "development")
      console.warn("Failed to refresh stale user data, returning cached:", error);
    // Return stale data rather than failing
    return user;
  }
};

/**
 * Get current user synchronously (without refresh check)
 * @deprecated Use getCurrentUser() instead for automatic freshness
 * @returns {Object|null} User object or null if not logged in
 */
export const getCurrentUserSync = () => {
  return user;
};

export const getApiToken = () => {
  return apiToken;
};

export const getCalendarToken = () => {
  return calendarToken;
};

/**
 * Refresh user data from server to get latest watchlist and other changes
 * @returns {Promise<Object>} Updated user object
 */
export const refreshUserData = async () => {
  if (!apiToken) {
    throw new Error("No API token available");
  }

  try {
    const data = await postJSON("/api/auth/refresh/", {
      token: apiToken,
    });

    if (data.status === "OK" && data.user) {
      changeUserData(data.user);
      triggerEvent("user_data_refreshed", { user: data.user });
      return data.user;
    }

    throw new Error("Failed to refresh user data");
  } catch (error) {
    if (process.env.NODE_ENV === "development") console.error("Error refreshing user data:", error);
    throw error;
  }
};

const loginSuccessful = (data, showNotification, timestamp) => {
  changeUserData(data.user, data.api_token, data.calendar_token, timestamp);
  triggerEvent("successful_login", {
    user: data.user,
    showNotification: !!showNotification,
  });
};

const logoutSuccessful = (dontRemoveCookie, showNotification) => {
  if (user?.token) clearUserData(user.token);
  triggerEvent("successful_logout", {
    dontRemoveCookie: !!dontRemoveCookie,
    showNotification: !!showNotification,
  });
};

const changeUserData = (newUser, newApiToken, newCalendarToken, timestamp) => {
  user = newUser;
  if (newApiToken) apiToken = newApiToken;
  if (newCalendarToken) calendarToken = newCalendarToken;
  userDataTimestamp = timestamp || Date.now(); // Use provided timestamp or current time
  try {
    window.sessionStorage.setItem(
      user.token,
      JSON.stringify({
        calendar_token: calendarToken,
        api_token: apiToken,
        user: user,
        timestamp: userDataTimestamp,
      })
    );
  } catch (e) {
    // sessionStorage is blocked or full, ignore
    if (process.env.NODE_ENV === "development") console.warn("Failed to save to sessionStorage", e);
  }
  triggerEvent("user_data_changed", {
    user: user,
  });
};

const clearUserData = (authToken) => {
  user = null;
  apiToken = null;
  calendarToken = null;
  try {
    window.sessionStorage.removeItem(authToken);
  } catch {
    // sessionStorage is blocked, ignore
  }
  triggerEvent("user_data_changed", {
    user: null,
  });
};

export const loginWithEmail = async (email) => {
  try {
    const data = await postJSON("/api/login/email/", {
      email: email,
      referer: window.location.href,
    });
    loginDialog.close();
    if (data.status == "registered") {
      openVerifyDialog(data);
    } else if (data.status == "not-consent") {
      openRegisterDialog(data.user, "Hozzájárulás", "Hozzájárulok");
    } else {
      openRegisterDialog({ email: email });
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") console.log("loginWithEmail", error);
    logoutSuccessful();
  }
};

export const loginWithAuthToken = async (authToken) => {
  if (authToken) {
    let authData;
    // We don't use this, because it stores watchlist state, that needs to be updated on every page load
    try {
      authData = JSON.parse(window.sessionStorage.getItem(authToken));
    } catch {
      // Invalid auth data in session storage
    }
    if (authData) {
      // Restore user data with preserved timestamp for staleness detection
      triggerEvent("login_by_cookie", {
        user: authData.user,
      });
      loginSuccessful(
        {
          user: authData.user,
          api_token: authData.api_token,
          calendar_token: authData.calendar_token,
        },
        false,
        authData.timestamp || Date.now()
      );
    } else {
      try {
        const data = await postJSON("/api/login/auth/", {
          auth_token: authToken,
        });
        if (data.status == "authenticated") {
          loginSuccessful(data);
        } else if (data.status == "not-consent") {
          openRegisterDialog(data.user, "Hozzájárulás", "Hozzájárulok");
        } else {
          logoutSuccessful(true);
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") console.log("loginWithAuthCookie", error);
        logoutSuccessful(true);
      }
    }
  } else {
    logoutSuccessful();
  }
};

export const handleVerifyByHash = async (verifyHash) => {
  try {
    const data = await postJSON("/api/verify/", {
      temporary_password_salted_hash: verifyHash,
    });
    if (data.status == "authenticated") {
      loginSuccessful(data, true);
    } else {
      if (process.env.NODE_ENV === "development") console.log("Verification failed", data);
      logoutSuccessful();
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") console.log("Verify error", error);
    logoutSuccessful();
  } finally {
    document.location.hash = "";
  }
};

export const toggleWatchlist = async (movieId) => {
  if (user.watchlist.filter((id) => id == movieId).length > 0) {
    return handleWatchlistAction("remove-from-watchlist", movieId);
  } else {
    return handleWatchlistAction("add-to-watchlist", movieId);
  }
};

const handleWatchlistAction = async (action, movieId) => {
  const data = await postJSON("/api/watchlist/", {
    token: apiToken,
    action: action,
    user_id: user.id,
    movie_id: movieId,
  });
  if (data.status == "OK") {
    if (action == "add-to-watchlist") {
      user.watchlist.push(movieId);
      triggerEvent("watchlist_movie_added", { movieId: movieId });
      changeUserData(user);
      return user;
    }
    if (action == "remove-from-watchlist") {
      user.watchlist = user.watchlist.filter((id) => id != movieId);
      triggerEvent("watchlist_movie_removed", { movieId: movieId });
      changeUserData(user);
      return user;
    }
  }
  throw data;
};

let eventListeners = [];

export const addAuthEventListener = (event, listener) => {
  event.split(" ").forEach((event) => {
    eventListeners.push({
      event: event,
      listener: listener,
    });
  });
  return listener;
};

export const triggerEvent = (event, payload) => {
  eventListeners.forEach((eventListener) => {
    if (eventListener.event == "all" || eventListener.event == event) {
      eventListener.listener(event, payload, this);
    }
  });
};

const authContainerSelector = "#login-container";
const loginContainerLoadedClass = "____loaded_" + Date.now();

const loadLoginHtml = async () => {
  if (!document.querySelector(authContainerSelector + "." + loginContainerLoadedClass)) {
    const response = await fetch("/api/login");
    const html = await response.text();
    document.querySelector(authContainerSelector).innerHTML = html;
    document.querySelector(authContainerSelector).className = loginContainerLoadedClass;
    triggerEvent("initialized", {});
  }
  return document.querySelector(authContainerSelector + "." + loginContainerLoadedClass);
};

let initializePromise = null;

const initialiseAuthContainer = () => {
  if (!initializePromise) {
    initializePromise = loadLoginHtml();
  }
  return initializePromise;
};

let loginDialog;
let registerDialog;
let verifyDialog;
let profileDialog;
let loginPromptDialog;

export const openLoginDialog = async () => {
  if (!loginDialog) {
    await initialiseAuthContainer();
    loginDialog = LoginDialog(document.querySelector("#login-bottom-sheet"), {
      onLoginWithEmail: (email) => loginWithEmail(email),
      onSuccessfulThirdPartyAuthentication: (user) => loginWithThirdPartyUser(user),
      onFailedThirdPartyAuthentication: (error) => handleThirdPartyAuthenticationFailure(error),
    }).mount();
  }
  loginDialog.open(window.config.googleClientId, window.config.facebookAppId);
};

export const openRegisterDialog = async (user, title, action) => {
  if (!registerDialog) {
    await initialiseAuthContainer();
    registerDialog = RegisterDialog(document.querySelector("#register-bottom-sheet"), {
      onSuccessfulRegistration: (data) => {
        registerDialog.close();
        openVerifyDialog(data);
      },
      onFailedRegistration: (error) => {
        if (process.env.NODE_ENV === "development") console.log("Registration failed", error);
      },
    }).mount();
  }
  registerDialog.open(user, title, action);
};

export const openVerifyDialog = async (data) => {
  if (!verifyDialog) {
    await initialiseAuthContainer();
    verifyDialog = VerifyDialog(document.querySelector("#verify-bottom-sheet"), {
      onSuccessfulVerification: (data) => {
        verifyDialog.close();
        loginSuccessful(data, true);
      },
      onFailedVerification: (data) => {
        if (process.env.NODE_ENV === "development") console.log("Verification failed", data);
        loginWithEmail(data.email);
      },
    }).mount();
  }
  verifyDialog.open(data.email, data.temporary_salt);
};

export const openProfileDialog = async () => {
  if (!profileDialog) {
    await initialiseAuthContainer();
    profileDialog = ProfileDialog(document.querySelector("#profil-bottom-sheet"), {
      onLogout: () => {
        profileDialog.close();
        logoutSuccessful(false, true);
      },
    }).mount();
  }
  profileDialog.open(user, apiToken, calendarToken);
};

export const openLoginPromptDialog = async (title, prompt) => {
  if (!loginPromptDialog) {
    await initialiseAuthContainer();
    loginPromptDialog = LoginPromptDialog(document.querySelector("#login-prompt-bottom-sheet"), {
      onSubmit: () => {
        loginPromptDialog.close();
        openLoginDialog();
      },
    }).mount();
  }
  loginPromptDialog.open(title, prompt);
};
